import { WebStorageStateStore } from "oidc-client-ts";

import { parseOidcUser } from "authentication/parseOidcUser";
import { getRefreshTokenCookie, removeRefreshTokenCookie, setRefreshTokenCookie } from "common/utilities/cookies";
import { logger } from "common/utilities/logger";
import { safeJsonParse } from "common/utilities/safeJsonParse";

export class OidcUserStore extends WebStorageStateStore {
  override set = (key: string, value: string): Promise<void> => {
    const user = parseOidcUser(value);

    if (user?.access_token !== undefined)
      fetch(
        `${import.meta.env.VITE_LOGIN_URL}/ssologin/setToken?token=${user.access_token}&apikey=${import.meta.env.VITE_AUTH_WIDGET_API_KEY}`,
        { method: "GET", credentials: "include" },
      ).catch((error: unknown) => logger.error("[OidcUserStore.set] Error occurred while setting token", error));

    if (user?.refresh_token !== undefined) setRefreshTokenCookie(user.refresh_token);

    return super.set(key, value);
  };

  override get = (key: string): Promise<string | null> => {
    const refreshToken = getRefreshTokenCookie();

    // If the refresh token cookie is missing then we are not authenticated, even if we have tokens in local storage
    if (refreshToken === undefined) {
      logger.log("[OidcUserStore.get] Refresh token cookie is missing");
      return Promise.resolve(null);
    }

    return super.get(key).then((value) => {
      // Overwrite the local refresh token as it may be outdated
      if (value === null) return Promise.resolve(JSON.stringify({ refresh_token: refreshToken }));
      const result = safeJsonParse(value);
      if (result === undefined) return Promise.resolve(JSON.stringify({ refresh_token: refreshToken }));
      const { value: parsed } = result;

      if (typeof parsed !== "object") {
        logger.error(`[OidcUserStore.get] Parsed value should be an object but is a ${typeof parsed}`, result);
        return Promise.resolve(JSON.stringify({ refresh_token: refreshToken }));
      }

      return Promise.resolve(JSON.stringify({ ...parsed, refresh_token: refreshToken }));
    });
  };

  override remove = (key: string): Promise<string | null> => {
    removeRefreshTokenCookie();
    return super.remove(key);
  };
}
